<template>
  <v-menu v-model="showMenu" :close-on-content-click="false" min-width="0" nudge-right="24" nudge-top="30" offset-y>
    <template v-slot:activator="{ on }">
      <div>
        <v-text-field
            ref="textField"
            v-model="textFieldValue"
            :class="textFieldClass"
            :clearable="clearable"
            :color="color"
            :dense="dense"
            :disabled="disabled || !isReady" :filled="filled"
            :hide-details="hideDetails"
            :hint="calcAge ? `${calcAge} Jahre alt` : hint"
            :label="label"
            :loading="loading || !isReady"
            :placeholder="placeholder"
            :outlined="outlined"
            :persistent-hint="birthdayMode || persistentHint"
            :prepend-icon="prependIcon"
            :rounded="rounded"
            :rules="rules"
            :shaped="shaped"
            :single-line="singleLine"
            :solo="solo"
            data-form-type="none"
            @blur="commitText"
            @focus="handleInputFocus"
            @input="parseDate"
            @keydown.enter="commitText"
            @click:clear="clear"
        >
          <template v-slot:append>
            <v-tooltip :disabled="showMenu" bottom>
              <template v-slot:activator="tooltip">
                <v-icon :disabled="disabled" @click="on.click" v-on="tooltip.on">mdi-calendar</v-icon>
              </template>
              Kalender anzeigen
            </v-tooltip>
          </template>
        </v-text-field>
        <v-input v-if="loading" v-model="loading"
                 :rules="[loadingRule]" readonly
                 style="margin-top: -22px;"/>
      </div>
    </template>
    <v-card>
      <v-date-picker
          ref="picker"
          v-model="tValue"
          :allowed-dates="allowedDates"
          :close-on-content-click="false"
          :color="color"
          :first-day-of-week="1"
          :max="max ? max : birthdayMode ? moment().format() : ''"
          :min="min"
          :reactive="true"
          :show-current="showCurrent"
          class="elevation-0"
          locale-first-day-of-year="4"
          no-title
          show-week
          @change="emitChange"
      ></v-date-picker>
      <div v-if="excludeHolidays && holidaysState" class="caption pt-0 px-2 pb-2">
        Ferien für {{ StateCodesVue.find((s) => s.value === holidaysState)?.text }} berücksichtigt
      </div>
      <div v-if="excludeHolidays && holidaysInstitution" class="caption pt-0 px-2 pb-2">
        Ferien der Schule berücksichtigt
      </div>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import Vue from 'vue';
import moment from 'moment';
import {RuleFactory} from '@/helpers/ruleFactory.helper';
import {isCompleteTypedDate} from '@/helpers/dateHelpers/typedTime.helper';
import {Holiday} from '@/interfaces/holiday.interface';
import {VacationGetterApi} from '@/classes/api/vacation-getter.api.class';
import {StateCodes, StateCodesVue} from '@/enums/StateCodes.enum';
import mongoose from 'mongoose';

moment.locale('de');

export default Vue.extend({
  props: {
    value: {
      type: Date,
    },
    rules: {
      type: Array as () => Array<(v: any) => boolean | string>,
      default: () => ([]),
    },
    birthdayMode: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'Datum',
    },
    /** Optional, e.g. with label="" when the label is rendered above the field */
    placeholder: {
      type: String,
      required: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
    },
    persistentHint: {
      type: Boolean,
      default: false,
    },
    max: {
      type: String,
    },
    min: {
      type: String,
    },
    format: {
      type: String,
      default: 'DD. MMMM YYYY',
    },
    excludedDates: {
      type: Array as () => Date[],
      default: () => ([]),
    },
    excludeHolidays: {
      type: Boolean,
      default: true,
    },
    holidaysState: {
      type: String as () => StateCodes,
      required: false,
    },
    holidaysInstitution: {
      type: mongoose.Types.ObjectId,
      required: false,
    },
    showCurrent: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      type: String,
      required: false,
    },
    appendIcon: {
      type: String,
      required: false,
    },
    color: {
      type: String,
      required: false,
    },
    dense: {
      type: Boolean,
      default: true,
    },
    hideDetails: {
      type: [Boolean, String],
      default: false,
    },
    filled: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    rounded: {
      type: Boolean,
      default: true,
    },
    singleLine: {
      type: Boolean,
      default: false,
    },
    shaped: {
      type: Boolean,
      default: false,
    },
    solo: {
      type: Boolean,
      default: false,
    },
    textFieldClass: {
      type: String,
      required: false,
    },
    openOnFocus: {
      type: Boolean as () => boolean | 'mobile' | 'desktop',
      required: false,
    },
  },
  data: () => ({
    tValue: '',
    showMenu: false,
    /** Debounce of incomplete typed input */
    pendingTimer: 0,
    loading: false,
    acceptedFormats: ['DD.MM.YYYY', 'DD. MMMM YYYY', 'D.MM.YYYY', 'DD.M.YYYY', 'D.M.YYYY'],
    textFieldValue: '',
    isReady: false,
    holidays: [] as Holiday[],
    loadingRule: function (v: boolean) {
      return !v || 'Bitte warte, bis die Ergebnisse geladen sind'
    },
  }),
  computed: {
    moment: () => moment,
    StateCodesVue: () => StateCodesVue,
    RuleFactory: () => RuleFactory,
    fValue: {
      get(): string {
        if (this.value) {
          return moment(this.value).format(this.format);
        } else {
          return '';
        }
      },
      set(val: string) {
        this.tValue = moment(val).format('YYYY-MM-DD');
      },
    },
    calcAge() {
      if (this.birthdayMode && this.tValue) {
        return moment().diff(moment(this.tValue), 'years');
      } else {
        return '';
      }
    },
    formattedExcludedDates(): string[] {
      const formattedDatesSet: Set<string> = new Set(this.excludedDates.map((date) => moment(date).format('YYYY-MM-DD')));
      if (this.excludeHolidays) {
        for (const holiday of this.holidays) {
          const current = moment(holiday.start);
          const end = moment(holiday.end).add(1, 'day');
          while (current.format('YYYY-MM-DD') !== end.format('YYYY-MM-DD')) {
            formattedDatesSet.add(current.format('YYYY-MM-DD'));
            current.add(1, 'day');
          }
        }
      }
      return Array.from(formattedDatesSet);
    },
  },
  methods: {
    handleInputFocus() {
      if (this.openOnFocus) {
        const shouldOpen = this.openOnFocus === true
            || (this.openOnFocus === 'mobile' && this.$vuetify.breakpoint.smAndDown)
            || (this.openOnFocus === 'desktop' && this.$vuetify.breakpoint.mdAndUp);
        if (shouldOpen) {
          this.showMenu = true;
        }
      }
    },
    allowedDates(val: string): boolean {
      return !this.formattedExcludedDates.includes(val);
    },
    updateTempValue() {
      if (this.value) {
        this.tValue = moment(this.value).format('YYYY-MM-DD');
      } else {
        this.tValue = '';
      }
      this.textFieldValue = this.fValue;
    },
    clear() {
      this.$emit('input', null);
    },
    emitChange() {
      this.$emit('input', moment(this.tValue).toDate());
      this.showMenu = false;
    },
    async loadHolidays() {
      this.isReady = false;
      try {
        this.holidays = [];
        if (this.excludeHolidays) {
          const holidays: Holiday[] = [];
          if (this.holidaysState) {
            const stateHolidays = await VacationGetterApi.find({
              skipPagination: true,
              filter: {
                stateCode: [this.holidaysState],
                range: {
                  start: moment().subtract(6, 'months').toDate(),
                  end: moment().add(6, 'months').toDate(),
                },
              },
            });
            holidays.push(...stateHolidays.holidays);
          }
          if (this.holidaysInstitution) {
            const institutionHolidays = await VacationGetterApi.find({
              skipPagination: true,
              filter: {
                institution: [this.holidaysInstitution],
                considerInstitutionStateAndCountry: true,
                range: {
                  start: moment().subtract(6, 'months').toDate(),
                  end: moment().add(6, 'months').toDate(),
                },
              },
            });
            holidays.push(...institutionHolidays.holidays);
          }
          this.holidays = holidays;
        }
      } finally {
        this.isReady = true;
      }
    },
    /** Typing: a complete date ("12.10.2026") is applied at once, anything else after a pause or on blur / Enter */
    parseDate() {
      this.clearPending();
      if (!this.textFieldValue) {
        this.$emit('input', null);
        return;
      }
      if (isCompleteTypedDate(this.textFieldValue)) {
        this.commitText();
        return;
      }
      this.loading = true;
      this.pendingTimer = window.setTimeout(() => this.commitText(), 1250);
    },
    clearPending() {
      if (this.pendingTimer) {
        window.clearTimeout(this.pendingTimer);
        this.pendingTimer = 0;
      }
      this.loading = false;
    },
    /** Applies the typed text (blur, Enter, complete input, debounce); unchanged text emits nothing */
    commitText() {
      this.clearPending();
      const text = (this.textFieldValue || '').trim();
      if (!text || text === this.fValue) {
        return;
      }
      for (const format of this.acceptedFormats) {
        const parsed = moment(text, format);
        if (parsed.isValid()) {
          // Show the applied value at once (the prop follows with the parent's next render)
          this.tValue = parsed.format('YYYY-MM-DD');
          this.textFieldValue = parsed.format(this.format);
          this.$emit('input', parsed.toDate());
          return;
        }
      }
      this.$$showSnackbar({text: 'Der eingegebene Text konnte nicht als Datum interpretiert werden'});
      this.textFieldValue = this.fValue;
    },
  },
  watch: {
    value() {
      this.updateTempValue();
    },
    showMenu(v: boolean) {
      if (v && this.birthdayMode) {
        setTimeout(() => {
          const picker = this.$refs.picker as {
            activePicker: string;
          } & HTMLElement;
          picker.activePicker = 'YEAR';
        });
      }
    },
    holidaysState() {
      this.loadHolidays();
    },
    holidaysInstitution() {
      this.loadHolidays();
    },
    excludeHolidays() {
      this.loadHolidays();
    },
    format() {
      this.parseDate();
    },
  },
  beforeDestroy() {
    this.clearPending();
  },
  mounted() {
    this.updateTempValue();
    this.loadHolidays();
    this.textFieldValue = this.fValue;
  },
});
</script>
